import React, { useState } from 'react';

// 더미 데이터
const dummyPublicProfile = {
    profileImage: 'https://via.placeholder.com/200',
    nickname: '코치김',
    handler: '@coach_kim',
    koachType: '연기 코치',
    averageRating: 4.8,
    totalReviews: 127,
    totalStudents: 245,
    bio: '10년 이상의 연기 경력을 바탕으로 학생들의 꿈을 응원합니다. 감정 표현부터 대본 분석까지 체계적으로 가르칩니다.',
    specialties: ['연기', '감정 표현', '대본 분석'],
    experience: '10년',
    education: '중앙대학교 연극영화과 졸업',
    availableLanguages: ['한국어', '영어'],
    classes: [
        {
            id: 1,
            title: '기초 연기 클래스',
            price: 100000,
            studentCount: 15,
            maxStudents: 20,
            rating: 4.9
        },
        {
            id: 2,
            title: '고급 댄스 클래스',
            price: 150000,
            studentCount: 8,
            maxStudents: 10,
            rating: 4.8
        }
    ],
    products: [
        {
            id: 1,
            title: '연기 기초 마스터 클래스',
            price: 99000,
            originalPrice: 149000,
            thumbnail: 'https://via.placeholder.com/200',
            sales: 125
        },
        {
            id: 2,
            title: 'K-pop 댄스 비디오 강의',
            price: 79000,
            originalPrice: 99000,
            thumbnail: 'https://via.placeholder.com/200',
            sales: 89
        }
    ],
    recentReviews: [
        {
            id: 1,
            studentName: '김학생',
            rating: 5,
            content: '정말 좋은 코치님이세요! 연기 실력이 많이 늘었어요.',
            date: '2025-01-10'
        },
        {
            id: 2,
            studentName: '박연습',
            rating: 5,
            content: '댄스 클래스가 너무 재미있었어요. 다음에도 또 들을 예정입니다!',
            date: '2025-01-08'
        }
    ]
};

const KoachProfilePreview = () => {
    const [profile] = useState(dummyPublicProfile);

    const renderStars = (rating: number) => {
        return Array.from({ length: 5 }, (_, i) => (
            <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>
                ★
            </span>
        ));
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">공개 프로필 미리보기</h1>
                
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {/* 프로필 헤더 */}
                    <div className="bg-gradient-to-r from-[#CF3688] to-[#B52E74] p-8 text-white">
                        <div className="flex items-start space-x-6">
                            <img 
                                src={profile.profileImage} 
                                alt={profile.nickname}
                                className="w-32 h-32 rounded-full object-cover border-4 border-white"
                            />
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold mb-1">{profile.nickname}</h2>
                                <p className="text-white/80 mb-2">{profile.handler}</p>
                                <p className="text-lg mb-4">{profile.koachType}</p>
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-1">
                                        {renderStars(Math.round(profile.averageRating))}
                                        <span className="ml-2 font-semibold">
                                            {profile.averageRating} ({profile.totalReviews})
                                        </span>
                                    </div>
                                    <span>•</span>
                                    <span>학생 {profile.totalStudents}명</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 프로필 본문 */}
                    <div className="p-6 space-y-6">
                        {/* 자기소개 */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">자기소개</h3>
                            <p className="text-gray-700 whitespace-pre-line">{profile.bio}</p>
                        </div>

                        {/* 전문 분야 */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">전문 분야</h3>
                            <div className="flex flex-wrap gap-2">
                                {profile.specialties.map((specialty, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-sm">
                                        {specialty}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* 경력 및 학력 */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <h4 className="text-sm font-medium text-gray-600 mb-1">경력</h4>
                                <p className="text-gray-900">{profile.experience}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-600 mb-1">학력</h4>
                                <p className="text-gray-900">{profile.education}</p>
                            </div>
                            <div>
                                <h4 className="text-sm font-medium text-gray-600 mb-1">가능 언어</h4>
                                <p className="text-gray-900">{profile.availableLanguages.join(', ')}</p>
                            </div>
                        </div>

                        {/* 클래스 */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">클래스</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {profile.classes.map((classItem) => (
                                    <div key={classItem.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-semibold text-gray-900">{classItem.title}</h4>
                                            <div className="flex items-center gap-1">
                                                {renderStars(Math.round(classItem.rating))}
                                            </div>
                                        </div>
                                        <p className="text-[#CF3688] font-semibold mb-2">
                                            {classItem.price.toLocaleString()}원
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            {classItem.studentCount} / {classItem.maxStudents}명
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 상품 */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">상품</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {profile.products.map((product) => (
                                    <div key={product.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                                        <img 
                                            src={product.thumbnail} 
                                            alt={product.title}
                                            className="w-full h-32 object-cover"
                                        />
                                        <div className="p-4">
                                            <h4 className="font-semibold text-gray-900 mb-2">{product.title}</h4>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[#CF3688] font-semibold">
                                                    {product.price.toLocaleString()}원
                                                </span>
                                                <span className="text-gray-500 line-through text-sm">
                                                    {product.originalPrice.toLocaleString()}원
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                판매 {product.sales}개
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 최근 리뷰 */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">최근 리뷰</h3>
                            <div className="space-y-4">
                                {profile.recentReviews.map((review) => (
                                    <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">{review.studentName}</span>
                                                <div className="flex items-center gap-1">
                                                    {renderStars(review.rating)}
                                                </div>
                                            </div>
                                            <span className="text-xs text-gray-500">{review.date}</span>
                                        </div>
                                        <p className="text-gray-700">{review.content}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KoachProfilePreview;

